import axios from "axios";
import showToaster from "../../../../helpers/toasterMessages/showToaster";


const parseCsvLine = (line) => {
    const trimmed = line.trim();
    if (!trimmed) return null;

    const result = [];
    let i = 0;

    while (i <= trimmed.length) {
        if (i === trimmed.length) break;

        if (trimmed[i] === '"') {
            let field = '';
            i++; // skip opening quote
            while (i < trimmed.length) {
                if (trimmed[i] === '"') {
                    if (trimmed[i + 1] === '"') {
                        field += '"';
                        i += 2;
                    } else {
                        i++; // skip closing quote
                        break;
                    }
                } else {
                    field += trimmed[i++];
                }
            }
            result.push(field.trim());
        } else {
            const comma = trimmed.indexOf(',', i);
            if (comma === -1) {
                result.push(trimmed.slice(i).trim());
                i = trimmed.length;
            } else {
                result.push(trimmed.slice(i, comma).trim());
                i = comma + 1;
            }
        }

        // skip comma between fields
        if (i < trimmed.length && trimmed[i] === ',') i++;
    }

    if (result.length !== 2) return null;
    return [result[0], result[1]];
};

export const spreadSheetImportHelper = ({ file, rowData, setRowData, languageCode, appType, isRestrict }) => {
    if (isRestrict) {
        showToaster({ messageId: 'demoWarning', toasterType: 'warning' })
        return;
    }

    if (!file.name.toLowerCase().endsWith('.csv')) {
        showToaster({ messageId: 'invalidCsvFileType', toasterType: 'error' });
        return;
    }

    const reader = new FileReader();

    reader.onload = async () => {
        try {
            let text = reader.result || '';
            text = text.replace(/^\uFEFF/, '');

            const rows = [];
            let current = '';
            let inQuote = false;

            for (let i = 0; i < text.length; i++) {
                const c = text[i];

                if (c === '"') {
                    if (inQuote && text[i + 1] === '"') {
                        current += '""';
                        i++;
                    } else {
                        inQuote = !inQuote;
                        current += c;
                    }
                } else if ((c === '\n' || c === '\r') && !inQuote) {
                    rows.push(current);
                    current = '';
                    if (c === '\r' && text[i + 1] === '\n') i++;
                } else {
                    current += c;
                }
            }

            rows.push(current);
            const lines = rows.map((r) => r.trim()).filter(Boolean);

            const dataRows = [];
            let startIndex = 0;

            if (lines.length > 0) {
                const first = lines[0].toLowerCase();
                if (first === 'key,value' || first === '"key","value"') startIndex = 1;
            }
            for (let i = startIndex; i < lines.length; i++) {
                const parsed = parseCsvLine(lines[i]);
                if (parsed === null) {
                    showToaster({ messageId: 'invalidCsvColumns', toasterType: 'error' });
                    return;
                }
                dataRows.push(parsed);
            }
            if (dataRows.length === 0) {
                showToaster({ messageId: 'emptyCsvSheet', toasterType: 'error' });
                return;
            }

            const keyFormat = /^[a-zA-Z0-9_]+$/i;
            const importedMap = {};
            for (const [key, value] of dataRows) {
                const k = key.trim();
                const v = value.trim();
                if (k === '' || v === '') {
                    showToaster({ messageId: 'keyValueRequiredError', toasterType: 'error' });
                    return;
                }

                if (!keyFormat.test(k)) {
                    showToaster({ messageId: 'mobileLanguageRegexError', toasterType: 'error', requestMessage: appType });
                    return;
                }

                importedMap[k] = v;
            }

            let existingMap = {};

            for (let row = 0; row < rowData?.length; row++) {
                if (rowData[row]?.[1]?.value !== undefined) {
                    const key = rowData[row][0]?.value?.toString()?.trim() || '';
                    const value = rowData[row][1]?.value?.toString()?.trim() || '';
                    if (key !== '') existingMap[key] = value;
                }
            }

            const merged = { ...existingMap, ...importedMap };

            const mergedRows = Object.entries(merged).map(([key, value]) => [
                { value: key, readOnly: true },
                { value }
            ]);

            setRowData(mergedRows);

            const modifiedData = {};

            for (const [key, value] of Object.entries(merged)) {
                let processed = value?.replace(/\\\\/g, '\\');
                const normalized = JSON.parse(JSON.stringify(processed));
                modifiedData[key] = normalized;
            }

            const params = JSON.stringify({
                data: modifiedData,
                languageCode,
                appType
            });

            const response = await axios.post('/save-language', params, {
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
            });

            if (response?.status === 200) {
                showToaster({ messageId: appType === 'web' ? 'webLanguageSuccess' : 'mobileLanguageSuccess', toasterType: 'success' });
            } else {
                showToaster({ messageId: 'mobileLanguageError', toasterType: 'error' });
            }
        } catch {
            await showToaster({ messageId: 'errorMessage', toasterType: 'error' });
        }
    };

    reader.readAsText(file);
};